import { useEffect, useState } from "react";
import { useCollection, useDocument } from "react-firebase-hooks/firestore";
import { db, DocumentData } from "api/firebase";
import { Message } from "ts/api/chat";

const MESSAGES_BATCH_SIZE = 50;

const useConversation = (showId: string, conversationId?: string) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [loadingOlderMessages, setLoadingOlderMessages] = useState(false);
  const [lastVisible, setLastVisible] = useState<DocumentData | null>(null);

  const conversationRef = conversationId
    ? db
        .collection("shows")
        .doc(showId)
        .collection("conversations")
        .doc(conversationId)
    : null;

  const [conversationDoc, loadingConversation, errorConversation] = useDocument(
    conversationRef
  );
  const conversation = conversationDoc?.data();

  const messagesQuery = conversationRef
    ? db
        .collection("shows")
        .doc(showId)
        .collection("conversations")
        .doc(conversationId)
        .collection("messages")
        .orderBy("date", "desc")
        .limit(MESSAGES_BATCH_SIZE)
    : null;

  const [messagesSnapshot, loadingMessages, errorMessages] = useCollection(
    messagesQuery
  );

  useEffect(() => {
    if (messagesSnapshot && !loadingMessages) {
      const newMessages = messagesSnapshot.docs
        .map((doc) => doc.data())
        .reverse();
      setMessages(newMessages as Message[]);
      const lastDoc = messagesSnapshot.docs[messagesSnapshot.docs.length - 1];
      setLastVisible(lastDoc);
    }
  }, [messagesSnapshot, loadingMessages]);

  const loadOlderMessages = async () => {
    if (!loadingOlderMessages && lastVisible) {
      setLoadingOlderMessages(true);

      const olderMessagesQuery = db
        .collection("shows")
        .doc(showId)
        .collection("conversations")
        .doc(conversationId)
        .collection("messages")
        .orderBy("date", "desc")
        .startAfter(lastVisible)
        .limit(MESSAGES_BATCH_SIZE);

      const snapshot = await olderMessagesQuery.get();

      if (!snapshot.empty) {
        const olderMessages = snapshot.docs.map((doc) => doc.data()).reverse();
        setMessages(
          (prevMessages) => [...olderMessages, ...prevMessages] as Message[]
        );

        setLastVisible(snapshot.docs[snapshot.docs.length - 1]);
      }

      setLoadingOlderMessages(false);
    }
  };

  const loadAllMessagesToId = async (messageId: string) => {
    if (!lastVisible || !conversationRef) return;

    setLoadingOlderMessages(true);

    const startAtMessage = await db
      .collection("shows")
      .doc(showId)
      .collection("conversations")
      .doc(conversationId)
      .collection("messages")
      .doc(messageId)
      .get();

    const betweenMessagesQuery = db
      .collection("shows")
      .doc(showId)
      .collection("conversations")
      .doc(conversationId)
      .collection("messages")
      .orderBy("date")
      .startAt(startAtMessage)
      .endBefore(messageId);

    const snapshot = await betweenMessagesQuery.get();

    if (!snapshot.empty) {
      const betweenMessages = snapshot.docs.map((doc) =>
        doc.data()
      ) as Message[];
      setMessages(betweenMessages);
      setLastVisible(startAtMessage);
    }
    setLoadingOlderMessages(false);
  };

  return {
    conversation,
    messages,
    loading: loadingConversation || loadingMessages,
    error: errorConversation || errorMessages,
    loadOlderMessages,
    loadAllMessagesToId,
  };
};

export default useConversation;
